import { useEffect } from 'react';
import {
  Edit, SimpleForm, TextInput, useGetList, Loading, useNotify,
  useDataProvider,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Stack, Typography, Box, Button, CircularProgress } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { ImageUploadInput } from '../../components/ImageUploadInput.jsx';
import { JourneyBandEditor } from '../../components/JourneyBandEditor.jsx';
import { AdmissionContentEditor } from '../../components/AdmissionContentEditor.jsx';
import { ContactInfoEditor } from '../../components/ContactInfoEditor.jsx';
import { useState } from 'react';

/**
 * SiteSettings is a singleton — exactly one row exists. The "list" view
 * immediately routes the user to the edit page for that one row.
 * If for some reason no row exists yet, we offer a one-click Create.
 */
const SiteSettingsRedirect = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data, isLoading } = useGetList('site-settings', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (data && data.length > 0) {
      navigate(`/site-settings/${data[0].id}`, { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) return <Loading />;
  if (data && data.length > 0) return null;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await dataProvider.create('site-settings', {
        data: { schoolName: 'Maple Leaf International School' },
      });
      navigate(`/site-settings/${created.data.id}`, { replace: true });
    } catch (err) {
      notify(`Failed to initialise site settings: ${err.message}`, { type: 'error' });
      setCreating(false);
    }
  };

  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Site settings not yet initialised</Typography>
      <Button variant="contained" onClick={handleCreate} disabled={creating}>
        {creating ? <CircularProgress size={18} /> : 'Initialise settings'}
      </Button>
    </Box>
  );
};

const SiteSettingsForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <Typography variant="overline" color="text.secondary">Branding</Typography>
    <TextInput source="schoolName" fullWidth />
    <TextInput
      source="brandTag"
      label="Brand tag"
      fullWidth
      helperText='Small line under the school name in the header — e.g. "International School · Est. 1972".'
    />
    <ImageUploadInput source="logo" label="Logo" />

    <Typography variant="overline" color="text.secondary">Contact</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="contactEmail" fullWidth />
      <TextInput source="contactPhone" fullWidth />
    </Stack>

    <Typography variant="overline" color="text.secondary">Footer</Typography>
    <TextInput source="footerText" multiline minRows={3} fullWidth />
    <TextInput source="footerCopyright" fullWidth />

    <Typography variant="overline" color="text.secondary">Social links</Typography>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="facebookUrl" label="Facebook" fullWidth />
      <TextInput source="instagramUrl" label="Instagram" fullWidth />
    </Stack>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextInput source="youtubeUrl" label="YouTube" fullWidth />
      <TextInput source="linkedinUrl" label="LinkedIn" fullWidth />
    </Stack>

    <Typography variant="overline" color="text.secondary" sx={{ pt: 2 }}>
      Homepage — "Start the journey" band
    </Typography>
    <JourneyBandEditor />

    <Typography variant="overline" color="text.secondary" sx={{ pt: 2 }}>
      Admission page content
    </Typography>
    <AdmissionContentEditor />

    <Typography variant="overline" color="text.secondary" sx={{ pt: 2 }}>
      Contact page — Visit us
    </Typography>
    <ContactInfoEditor />
  </Stack>
);

const SiteSettingsEdit = () => (
  <Edit redirect={false} mutationMode="pessimistic" actions={false}>
    <SimpleForm><SiteSettingsForm /></SimpleForm>
  </Edit>
);

export default {
  list: SiteSettingsRedirect,
  edit: SiteSettingsEdit,
  icon: SettingsIcon,
  options: { label: 'Site settings' },
};
